import {
	Requirement,
	VehicleProject,
	VehicleProjectMaster,
	VexCluster,
} from 'types';
import {TFunction} from 'react-i18next';
import {
	IHTMLXL,
	IArrayRoles,
	renderHeaderXL,
	renderHTMLExcel,
	cleanHTMLExcel,
	createZIPXL,
	addXLTable,
	addHTMLImagesAsLinks,
} from './ExcelExportCommon';
import Excel from 'exceljs';
import {formatDateTime} from 'i18n/localeDateFormat';
import i18n from 'i18n/i18n';
import {
	AssignedRequirementWithID,
	resolveVehicleProjects,
} from 'features/VehicleProjects/AssignedEntities/AssignedRequirementColumns';
import {renderRichtText} from 'components/EntityList/Excel/ExcelExportCommon';
import {
	getBodyWorkVariants,
	getDriveVariants,
	getEarliestPosEtVsi,
	getProjectName,
	getTechnologyVariants,
} from 'features/VehicleProjects/components/vehicleProjectColumns';

interface IAssignedRequirementList {
	(
		entityListItems: AssignedRequirementWithID[],
		columnNames: string[],
		dictVehicleProjects: Record<string, VehicleProject>,
		vehicleProjectMaster: VehicleProjectMaster | undefined,
		t: TFunction,
	): Promise<void>;
}

export const handleClickAssignedRequirements: IAssignedRequirementList = async (
	entityListItems,
	columnNames,
	dictVehicleProjects,
	vehicleProjectMaster,
	t,
) => {
	const filtRequirement = entityListItems.map(x =>
		resolveVehicleProjects(x, dictVehicleProjects),
	);

	const arrImagesUri: string[] = [];
	const arrImagesName: string[] = [];
	const arrTables: string[] = [];

	const tOptionsReq = {
		ns: 'features/vehicleprojects',
	};

	const tOptions = {
		ns: 'features/vehicleprojects',
		keyPrefix: 'AssignedRequirementsFields',
	};

	const tOptionsRelevanz = {
		ns: 'features/vehicleprojects',
		keyPrefix: 'ProjectRelevantFields',
	};

	const workbook = new Excel.Workbook();
	const sheet = workbook.addWorksheet(
		'TRACE_' + t('AssignedRequirements', tOptionsReq),
	);
	const columns = columnNames.map(x => {
		return {name: x, filterButton: true};
	});
	const arrColWidth: number[] = [
		40,
		...new Array(4).fill(25),
		40,
		...new Array(6).fill(25),
		80,
		...new Array(10).fill(25),
	];
	const strPageName = '';

	renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

	let intRowCount = 4;
	let row = sheet.getRow(intRowCount);

	row.alignment = {wrapText: true, vertical: 'top'};
	const derivates = (vehicleProjectMaster?.derivates ?? [])
		.filter(x => x.trim() !== '')
		.join(';\n');
	const sOP = vehicleProjectMaster?.sopVsi
		? formatDateTime(new Date(vehicleProjectMaster?.sopVsi), i18n)
		: '';
	const eOP = vehicleProjectMaster?.eopVsi
		? formatDateTime(new Date(vehicleProjectMaster?.eopVsi), i18n)
		: '';
	const lastEvaluated = vehicleProjectMaster?.lastEvaluated
		? formatDateTime(new Date(vehicleProjectMaster?.lastEvaluated), i18n)
		: '';

	row.getCell('B').value = renderRichtText(
		t('Derivative', tOptionsReq),
		derivates,
	);
	row.getCell('C').value = renderRichtText(t('EarliestSOP', tOptionsReq), sOP);
	row.getCell('D').value = renderRichtText(t('LatestEOP', tOptionsReq), eOP);
	row.getCell('F').value = renderRichtText(
		t('LastEvaluated', tOptionsReq),
		lastEvaluated,
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);

	const vehicleProjects: VehicleProject[] = Object.values(dictVehicleProjects);
	const bwvr = getBodyWorkVariants(vehicleProjects).join(';\n');
	const dvr = getDriveVariants(vehicleProjects).join(';\n');
	const pos = getEarliestPosEtVsi(vehicleProjects) ?? '';
	const technology = getTechnologyVariants(vehicleProjects).join(';\n');

	row.getCell('B').value = renderRichtText(
		t('BodyworkVariants', tOptionsReq),
		bwvr,
	);
	row.getCell('C').value = renderRichtText(
		t('DriveVariants', tOptionsReq),
		dvr,
	);
	row.getCell('D').value = renderRichtText(t('EarliestPOS', tOptionsReq), pos);
	row.getCell('F').value = renderRichtText(
		t('Technology', tOptionsReq),
		technology,
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.getCell('B').value = renderRichtText(
		t('VehicleProjects', tOptionsReq),
		vehicleProjects.map(x => getProjectName(x, i18n)).join(';\n'),
	);

	intRowCount++;
	intRowCount++;
	row = sheet.getRow(intRowCount);

	row.font = {
		bold: true,
		name: 'Porsche Next TT',
		size: 10,
		color: {argb: 'FFFFFFFF'},
	};

	const tableName = 'tableRequirements';

	addXLTable(tableName, intRowCount, sheet, columns);

	const table = sheet.getTable(tableName);

	intRowCount++;
	const tempDivElement = document.createElement('div');
	let arrHTMLXL: IHTMLXL[];
	let arrHTMLXLClean: IHTMLXL[];

	if (filtRequirement.length === 0) {
		table.addRow(new Array(columnNames.length).fill(''));
		intRowCount++;
	}

	for (let ii = 0; ii < filtRequirement.length; ii++) {
		arrHTMLXL = [];
		arrHTMLXLClean = [];
		const iAssignedReq = filtRequirement[ii];
		tempDivElement.innerHTML = iAssignedReq.definition
			? '<div>' + iAssignedReq.definition + '</div>'
			: '<div></div>';
		renderHTMLExcel(tempDivElement.childNodes[0].childNodes, arrHTMLXL);
		cleanHTMLExcel(arrHTMLXL, arrHTMLXLClean);

		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};
		row.getCell('H').style.font = {
			underline: true,
			bold: false,
			color: {argb: '00B0F0'},
		};
		table.addRow([
			iAssignedReq.vehicleProjects
				.map(vp => getProjectName(vp, i18n))
				.join(';\n'),
			iAssignedReq.isProjectRelevant
				? t('Yes', tOptionsRelevanz)
				: t('No', tOptionsRelevanz),
			iAssignedReq.projectRelevantComments ?? '',
			iAssignedReq.addedAutomatically
				? t('true', tOptions)
				: t('false', tOptions),
			iAssignedReq.addReason === 'AutomaticAssignment'
				? t('AutomaticAssignment', tOptions)
				: iAssignedReq.addReason,
			iAssignedReq.name,
			{
				text: iAssignedReq.requirementId,
				hyperlink: window.location.origin + '/requirements/' + iAssignedReq.id,
			},
			iAssignedReq.version ?? '',
			iAssignedReq.vexClusters.map(cluster => cluster.name).join(';\n'),
			iAssignedReq.dateEffective
				? formatDateTime(new Date(iAssignedReq.dateEffective), i18n)
				: '',
			iAssignedReq.dateEffective
				? formatDateTime(new Date(iAssignedReq.dateExpiration), i18n)
				: '',
			t(iAssignedReq.status ?? '', {
				ns: 'common/enums',
				keyPrefix: 'RequirementStatus',
			}),
			{richText: arrHTMLXLClean, wrapText: true, vertical: 'top'},
			iAssignedReq.systemLevels.map(level => level.name).join(';\n'),
			iAssignedReq.markets.map(market => market.name).join(';\n'),
			iAssignedReq.keywords.map(keyword => keyword.name).join(';\n'),
			iAssignedReq.tags.map(tags => tags.name).join(';\n'),
			iAssignedReq.createdBy.name,
			iAssignedReq.modifiedAt
				? formatDateTime(new Date(iAssignedReq.modifiedAt), i18n)
				: '',
			t(iAssignedReq.category, {
				ns: 'common/enums',
				keyPrefix: 'RequirementCategory',
			}),
		]);

		intRowCount = addHTMLImagesAsLinks(
			intRowCount,
			iAssignedReq.requirementId as string,
			arrImagesName,
			arrImagesUri,
			tempDivElement,
			sheet,
			row,
			table,
			t,
		);
	}

	table.commit();

	const strZipName = t('RequirementsHeader', {ns: 'features/requirements'});
	await createZIPXL(
		workbook,
		strZipName,
		t,
		arrTables,
		arrImagesUri,
		arrImagesName,
		[],
	);
};
